import { getDocumentImageBlob } from '@/api/content-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useHubBranding, useUpdateHubBranding, useUser } from '@/hooks';
import { HubBrandingUpdateInput } from '@vivport/backend/types';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { SettingsSection, SettingsSectionBody } from './SettingsPage';

function LogoPreview({ code }: { code: string }) {
  const { data: blob, isError } = useQuery({
    queryKey: ['hub', 'branding', 'logo', code],
    queryFn: () => getDocumentImageBlob(code),
    retry: false,
  });
  const objectUrl = useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob]);
  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);
  if (isError) {
    return <span className="text-sm text-muted-foreground italic">Logo image could not be loaded ({code})</span>;
  }
  if (!objectUrl) return null;
  return <img src={objectUrl} alt="Hub logo" className="max-h-[60px]" />;
}

export function HubBrandingSection() {
  const { data: memberContext } = useUser();
  const brandingQuery = useHubBranding();
  const updateMutation = useUpdateHubBranding();

  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [addressText, setAddressText] = useState('');
  const [logoCode, setLogoCode] = useState('');

  const branding = brandingQuery.data;
  useEffect(() => {
    if (!branding) return;
    setPhone(branding.phone ?? '');
    setWebsite(branding.website ?? '');
    setAddressText(branding.addressLines.join('\n'));
    setLogoCode(branding.logoContentCode ?? '');
  }, [branding]);

  if (memberContext?.selectedMember?.isAdmin !== true) {
    return null;
  }

  const handleSave = () => {
    if (!branding) return;
    const nextLines = addressText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    const input: HubBrandingUpdateInput = {};
    if ((phone.trim() || null) !== branding.phone) {
      input.phone = phone.trim() ? phone.trim() : null;
    }
    if ((website.trim() || null) !== branding.website) {
      input.website = website.trim() ? website.trim() : null;
    }
    if (nextLines.join('\n') !== branding.addressLines.join('\n')) {
      input.addressLines = nextLines;
    }
    if ((logoCode.trim() || null) !== branding.logoContentCode) {
      input.logoContentCode = logoCode.trim() ? logoCode.trim() : null;
    }
    if (Object.keys(input).length === 0) {
      toast.info('No branding changes to save');
      return;
    }
    updateMutation.mutate(input);
  };

  return (
    <SettingsSection
      title="Hub letterhead"
      description="These details appear on every exported document: the logo (or hub name) as the letterhead and the contact line in the footer. The logo is the content code of an image already uploaded to this hub."
    >
      <SettingsSectionBody className="max-w-xl">
        <div>
          <Label htmlFor="branding-phone">Phone</Label>
          <Input
            id="branding-phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="e.g. +61 2 5550 1234"
          />
        </div>
        <div>
          <Label htmlFor="branding-website">Website</Label>
          <Input
            id="branding-website"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="e.g. example.com"
          />
        </div>
        <div>
          <Label htmlFor="branding-address">Address (one line per row)</Label>
          <Textarea
            id="branding-address"
            value={addressText}
            onChange={(e) => setAddressText(e.target.value)}
            rows={3}
            placeholder={'1 Example Street\nExampleville NSW 2000'}
          />
        </div>
        <div>
          <Label htmlFor="branding-logo">Logo content code</Label>
          <Input
            id="branding-logo"
            value={logoCode}
            onChange={(e) => setLogoCode(e.target.value)}
            placeholder="Content code of an image in this hub"
          />
          {logoCode.trim().length > 0 ? (
            <div className="mt-2">
              <LogoPreview code={logoCode.trim()} />
            </div>
          ) : null}
        </div>
        <Button variant="outline" onClick={handleSave} disabled={updateMutation.isPending || brandingQuery.isLoading}>
          {updateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Save letterhead
        </Button>
      </SettingsSectionBody>
    </SettingsSection>
  );
}
